/* =========================================================================
   ClueVine — Site config (the admin-managed runtime settings)
   Owner-facing knobs that change how the live site behaves without a rebuild:
   which modes are enabled, the mode-of-the-day feature, and the monetization
   surfaces (the one ad + the support / tip links). Defaults come from
   src/config.ts; changes made in the admin are stored on THIS device only (they
   preview the site for the owner — they don't reach other visitors). Only the
   values actually changed are stored, so a redeploy with new config.ts values
   still reaches every returning player. A clean seam for a future server-backed
   config: swap load/save here for a fetch to your server.
   ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ADS_ENABLED, ADSENSE_CLIENT, ADSENSE_SLOT, SUPPORT_URL, TIP_URL } from '../config';
import { loadSlice, saveSlice, type SliceConfig } from './persistence';

export interface SiteConfig {
  /** modeId -> enabled. A missing key means enabled (new modes are on by default). */
  enabledModes: Record<string, boolean>;
  showModeOfDay: boolean;
  adsEnabled: boolean;
  adsClient: string;
  adsSlot: string;
  supportUrl: string;
  tipUrl: string;
}

export const DEFAULT_SITE_CONFIG: SiteConfig = {
  enabledModes: {},
  showModeOfDay: true,
  adsEnabled: ADS_ENABLED,
  adsClient: ADSENSE_CLIENT,
  adsSlot: ADSENSE_SLOT,
  supportUrl: SUPPORT_URL,
  tipUrl: TIP_URL,
};

/** Stored overrides: only the fields that differ from DEFAULT_SITE_CONFIG. */
const slice: SliceConfig<Partial<SiteConfig>> = {
  name: 'site-config',
  version: 2,
  defaults: () => ({}),
  // v1 stored the whole config, freezing every default at first visit. Keep only
  // the owner's deliberate choices (mode toggles + mode-of-the-day).
  migrate: (from, data) => {
    if (from !== 1 || !data || typeof data !== 'object') return null;
    const d = data as Partial<SiteConfig>;
    const out: Partial<SiteConfig> = {};
    if (d.enabledModes && typeof d.enabledModes === 'object') out.enabledModes = d.enabledModes;
    if (typeof d.showModeOfDay === 'boolean' && d.showModeOfDay !== DEFAULT_SITE_CONFIG.showModeOfDay) {
      out.showModeOfDay = d.showModeOfDay;
    }
    return out;
  },
};

function overridesOf(config: SiteConfig): Partial<SiteConfig> {
  const out: Partial<SiteConfig> = {};
  for (const key of Object.keys(DEFAULT_SITE_CONFIG) as (keyof SiteConfig)[]) {
    const value = config[key];
    const same =
      key === 'enabledModes'
        ? Object.values(value as Record<string, boolean>).every((v) => v !== false)
        : value === DEFAULT_SITE_CONFIG[key];
    if (!same) (out as Record<string, unknown>)[key] = value;
  }
  return out;
}

interface SiteConfigContextValue {
  config: SiteConfig;
  update: (patch: Partial<SiteConfig>) => void;
  setModeEnabled: (id: string, enabled: boolean) => void;
  isModeEnabled: (id: string) => boolean;
  reset: () => void;
}

const SiteConfigContext = createContext<SiteConfigContextValue | null>(null);

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(() => ({
    ...DEFAULT_SITE_CONFIG,
    ...loadSlice(slice),
  }));

  useEffect(() => {
    saveSlice(slice, overridesOf(config));
  }, [config]);

  const update = useCallback(
    (patch: Partial<SiteConfig>) => setConfig((c) => ({ ...c, ...patch })),
    [],
  );
  const setModeEnabled = useCallback(
    (id: string, enabled: boolean) =>
      setConfig((c) => ({ ...c, enabledModes: { ...c.enabledModes, [id]: enabled } })),
    [],
  );
  const isModeEnabled = useCallback(
    (id: string) => config.enabledModes[id] !== false,
    [config.enabledModes],
  );
  const reset = useCallback(() => setConfig({ ...DEFAULT_SITE_CONFIG }), []);

  const value = useMemo<SiteConfigContextValue>(
    () => ({ config, update, setModeEnabled, isModeEnabled, reset }),
    [config, update, setModeEnabled, isModeEnabled, reset],
  );

  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

export function useSiteConfig(): SiteConfigContextValue {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error('useSiteConfig must be used within <SiteConfigProvider>');
  return ctx;
}
